// /account/security — 2FA setup + sessions list + revoke
import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, KeyRound, RefreshCw, LogOut, Smartphone, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import Brand from "../components/Brand";

const Security = () => {
    const { user, refresh } = useAuth();
    const [setup, setSetup] = useState(null); // {secret, qr_uri, backup_codes}
    const [verifyCode, setVerifyCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [showDisable, setShowDisable] = useState(false);
    const [disableForm, setDisableForm] = useState({ password: "", token: "" });
    const [copied, setCopied] = useState(false);

    const enabled = !!user?.twofa?.enabled;

    const loadSessions = async () => {
        try {
            const { data } = await api.get("/auth/sessions");
            setSessions(data.items || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { loadSessions(); }, []);

    const startSetup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/setup");
            setSetup(data);
            toast.success("Scan the QR with Google Authenticator / Authy / 1Password");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Setup failed");
        } finally { setBusy(false); }
    };

    const enable = async () => {
        if (!verifyCode.trim() || verifyCode.length < 6) { toast.error("Enter the 6-digit code from your app"); return; }
        setBusy(true);
        try {
            await api.post("/auth/2fa/verify-and-enable", { token: verifyCode.trim() });
            toast.success("Two-factor authentication enabled. Keep your backup codes safe.");
            setSetup(null);
            setVerifyCode("");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Wrong code. Try again.");
        } finally { setBusy(false); }
    };

    const disable = async () => {
        if (!disableForm.password) { toast.error("Password required"); return; }
        setBusy(true);
        try {
            await api.post("/auth/2fa/disable", disableForm);
            toast.success("Two-factor authentication disabled.");
            setShowDisable(false);
            setDisableForm({ password: "", token: "" });
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not disable.");
        } finally { setBusy(false); }
    };

    const regenerateBackup = async () => {
        const password = window.prompt("Confirm your password to regenerate backup codes:");
        if (!password) return;
        setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/regenerate-backup-codes", { password });
            setSetup({ ...setup, backup_codes: data.backup_codes });
            toast.success("New backup codes generated — save them now!");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not regenerate.");
        } finally { setBusy(false); }
    };

    const revokeSession = async (sid) => {
        if (!window.confirm("Revoke this session? It will be signed out immediately.")) return;
        try {
            await api.post(`/auth/sessions/${sid}/revoke`);
            toast.success("Session revoked");
            loadSessions();
        } catch { toast.error("Could not revoke"); }
    };

    const revokeAll = async () => {
        if (!window.confirm("Sign out from all other devices? Your current session stays active.")) return;
        try {
            const { data } = await api.post("/auth/sessions/revoke-all");
            toast.success(`${data.revoked} session(s) revoked`);
            loadSessions();
        } catch { toast.error("Could not revoke"); }
    };

    const copyCodes = () => {
        if (!setup?.backup_codes) return;
        navigator.clipboard?.writeText(setup.backup_codes.join("\n"));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="bg-zinc-50 min-h-screen pb-24">
            <header className="border-b border-zinc-100 bg-white">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
                    <Link to="/account" className="text-sm text-zinc-600 hover:text-brand-700">← Back to account</Link>
                    <Brand size="sm" />
                </div>
            </header>

            <main className="mx-auto max-w-3xl space-y-6 px-6 py-8" data-testid="security-page">
                <h1 className="text-3xl font-semibold tracking-tight">Security</h1>
                <p className="text-sm text-zinc-500">Protect your account with two-factor authentication and manage active sessions.</p>

                {/* 2FA card */}
                <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-soft" data-testid="security-twofa-card">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <div className={`grid h-10 w-10 place-items-center rounded-xl ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                                {enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
                            </div>
                            <div>
                                <p className="label-uppercase">Two-factor authentication</p>
                                <h2 className="text-lg font-semibold tracking-tight">{enabled ? "Enabled" : "Not enabled"}</h2>
                                <p className="mt-1 text-xs text-zinc-500">{enabled ? "You'll be asked for a code from your authenticator app on every login." : "Add a second layer of security — required for admin & pharmacist accounts."}</p>
                            </div>
                        </div>
                        {enabled ? (
                            <button onClick={() => setShowDisable(true)} className="rounded-md border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50" data-testid="security-twofa-disable">Disable</button>
                        ) : (
                            <button onClick={startSetup} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="security-twofa-start">
                                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
                                Set up 2FA
                            </button>
                        )}
                    </div>

                    {setup && !enabled && (
                        <div className="mt-5 space-y-4 rounded-xl border border-brand-200 bg-brand-50/40 p-4" data-testid="security-twofa-setup">
                            <div>
                                <p className="text-sm font-semibold text-brand-800">1. Scan with your authenticator app</p>
                                <p className="text-xs text-slate-600 mt-1">Google Authenticator, Authy, 1Password, etc.</p>
                                <div className="mt-3 flex items-center gap-4 rounded-md bg-white p-3">
                                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(setup.qr_uri)}`} alt="QR" className="h-32 w-32" data-testid="security-twofa-qr" />
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Or enter manually</p>
                                        <code className="mt-1 block break-all rounded bg-zinc-50 p-2 font-mono text-xs">{setup.secret}</code>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-brand-800">2. Save your backup codes</p>
                                <p className="text-xs text-slate-600 mt-1">Use these single-use codes if you lose your device. Each works once.</p>
                                <div className="mt-2 grid grid-cols-2 gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs font-mono" data-testid="security-backup-codes">
                                    {setup.backup_codes.map((c) => <code key={c}>{c}</code>)}
                                </div>
                                <button onClick={copyCodes} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-brand-700 hover:underline">
                                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                    {copied ? "Copied" : "Copy all"}
                                </button>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-brand-800">3. Verify & enable</p>
                                <p className="text-xs text-slate-600 mt-1">Enter the current 6-digit code from your app:</p>
                                <div className="mt-2 flex gap-2">
                                    <input
                                        value={verifyCode}
                                        onChange={(e) => setVerifyCode(e.target.value)}
                                        placeholder="123456"
                                        maxLength={6}
                                        className="w-32 rounded-md border border-zinc-200 px-3 py-2 font-mono tracking-widest"
                                        data-testid="security-verify-input"
                                    />
                                    <button onClick={enable} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="security-verify-submit">
                                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                                        Enable
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {enabled && (
                        <div className="mt-4 flex justify-end">
                            <button onClick={regenerateBackup} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50" data-testid="security-twofa-regenerate">
                                <RefreshCw className="h-3 w-3" /> Regenerate backup codes
                            </button>
                        </div>
                    )}

                    {showDisable && (
                        <div className="mt-4 space-y-2 rounded-xl border border-rose-200 bg-rose-50/40 p-4">
                            <p className="text-sm font-semibold text-rose-800">Disable 2FA</p>
                            <p className="text-xs text-rose-700">Enter your password and current 6-digit code to confirm.</p>
                            <input type="password" placeholder="Your password" value={disableForm.password} onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })} className="w-full rounded-md border border-zinc-200 px-2 py-1.5 text-sm" data-testid="security-disable-password" />
                            <input value={disableForm.token} onChange={(e) => setDisableForm({ ...disableForm, token: e.target.value })} placeholder="6-digit code" maxLength={6} className="w-full rounded-md border border-zinc-200 px-2 py-1.5 font-mono text-sm" data-testid="security-disable-token" />
                            <div className="flex gap-2">
                                <button onClick={() => setShowDisable(false)} className="flex-1 rounded-md border border-zinc-200 px-3 py-1.5 text-xs">Cancel</button>
                                <button onClick={disable} disabled={busy} className="flex-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50" data-testid="security-disable-submit">
                                    {busy ? <Loader2 className="mx-auto h-3 w-3 animate-spin" /> : "Disable 2FA"}
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* Sessions card */}
                <section className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-soft" data-testid="security-sessions-card">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-100 text-brand-700">
                                <Smartphone className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="label-uppercase">Active sessions</p>
                                <h2 className="text-lg font-semibold tracking-tight">{sessions.filter((s) => !s.revoked_at).length} active</h2>
                                <p className="text-xs text-zinc-500">Devices currently signed in to your account.</p>
                            </div>
                        </div>
                        <button onClick={revokeAll} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50" data-testid="security-revoke-all">
                            <LogOut className="h-3 w-3" /> Sign out other devices
                        </button>
                    </div>
                    <ul className="mt-4 divide-y divide-zinc-100" data-testid="security-sessions-list">
                        {sessions.map((s) => (
                            <li key={s.id} className="flex items-center justify-between gap-3 py-3" data-testid={`security-session-${s.id}`}>
                                <div>
                                    <p className="text-sm font-medium">{s.device_name || "Unknown device"}
                                        {s.is_current && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">CURRENT</span>}
                                        {s.revoked_at && <span className="ml-2 rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-semibold text-zinc-700">REVOKED</span>}
                                    </p>
                                    <p className="text-[11px] text-zinc-500">{s.ip || "—"} · last seen {new Date(s.last_seen_at).toLocaleString()}</p>
                                </div>
                                {!s.revoked_at && !s.is_current && (
                                    <button onClick={() => revokeSession(s.id)} className="rounded-md border border-rose-200 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50" data-testid={`security-revoke-${s.id}`}>Revoke</button>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </main>
        </div>
    );
};

export default Security;
